'use client';

import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  getGridAvailableTeams,
  getTeamGroupLabels,
} from '@/api/tournament/tournamentApi';
import type { GridAvailableTeams } from '@/types/tournament.model';
import { formatDateForUser } from '@/utils/formatters';
import { logError } from '@/utils/logger';
import { teamLabel, type GridSlotRef } from './grid-model';

interface GridAvailableTeamsDialogProps {
  tournamentId: string;
  /** `null` keeps the dialog closed. */
  slot: GridSlotRef | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * What came back, and for which slot. Keyed by slot so that reopening the dialog on another one
 * reads as loading right away, instead of flashing the previous slot's list for a render.
 */
type Result =
  | { slot: GridSlotRef; status: 'error' }
  | {
      slot: GridSlotRef;
      status: 'loaded';
      groups: GridAvailableTeams[];
      /** teamId -> "Grupo B", across every tournament in `groups`. */
      teamGroups: Map<string, string>;
    };

/**
 * Lists every pair, across this tournament and its siblings, that declared it can play in a slot.
 * It helps the organizer find a match to put in an open slot without opening each pair's
 * availability one by one.
 *
 * The answer comes from `GET .../shared-grid/available-teams`, whose fit rule is the backend's
 * `TeamAvailabilityFit` — the same one `fitsAvailability` follows for the grid's own warnings.
 *
 * That endpoint doesn't say which group each pair is in, so the groups fixture of every tournament
 * in the answer is fetched too. It's a nicety: a failure there just leaves the pairs unlabeled.
 */
export function GridAvailableTeamsDialog({
  tournamentId,
  slot,
  onOpenChange,
}: GridAvailableTeamsDialogProps) {
  const [result, setResult] = useState<Result | null>(null);
  // Groups don't change while the grid is open (nothing here moves a pair between groups), so each
  // tournament's are fetched once per page, not on every slot the organizer opens.
  const teamGroupsCache = useRef(new Map<string, Promise<Map<string, string>>>());

  // The dialog animates out after `slot` turns null. Rendering from the last open slot keeps the
  // title and the list in place during the fade, instead of flipping to "¿Quién puede?" and
  // "Buscando parejas..." on the way out.
  const [lastSlot, setLastSlot] = useState(slot);
  if (slot !== null && slot !== lastSlot) setLastSlot(slot);
  const shownSlot = slot ?? lastSlot;

  useEffect(() => {
    if (!slot) return;
    // Guards against a slow answer for a slot the organizer already closed or switched away from
    // overwriting the current one.
    let cancelled = false;
    const teamGroupsOf = (id: string) => {
      const cache = teamGroupsCache.current;
      let labels = cache.get(id);
      if (!labels) {
        labels = getTeamGroupLabels(id).catch((err: unknown) => {
          logError('GridAvailableTeamsDialog.loadGroups', err);
          // Not cached: the next slot opened gets to try again.
          cache.delete(id);
          return new Map<string, string>();
        });
        cache.set(id, labels);
      }
      return labels;
    };
    // The current tournament is always in the answer: its groups load alongside it, not after.
    void teamGroupsOf(tournamentId);
    getGridAvailableTeams(tournamentId, slot.date, slot.time)
      .then(async (groups) => {
        const perTournament = await Promise.all(
          groups.map((group) => teamGroupsOf(group.tournamentId)),
        );
        const teamGroups = new Map(perTournament.flatMap((m) => [...m]));
        if (!cancelled)
          setResult({ slot, status: 'loaded', groups, teamGroups });
      })
      .catch((err) => {
        logError('GridAvailableTeamsDialog.load', err);
        if (!cancelled) setResult({ slot, status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [tournamentId, slot]);

  const state =
    result && result.slot === shownSlot ? result : { status: 'loading' as const };

  return (
    <Dialog open={slot !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {shownSlot
              ? `¿Quién puede el ${formatDateForUser(shownSlot.date, 'EEE dd/MM')} a las ${shownSlot.time}?`
              : '¿Quién puede?'}
          </DialogTitle>
          <DialogDescription>
            Parejas que declararon que pueden jugar en este horario. No se cruza
            con los partidos: puede aparecer una pareja que ya juega a esa hora.
          </DialogDescription>
        </DialogHeader>

        {state.status === 'loading' && (
          <p className="text-sm text-gray-500">Buscando parejas...</p>
        )}
        {state.status === 'error' && (
          <p className="text-sm text-red-600">
            No se pudieron cargar las parejas.
          </p>
        )}
        {state.status === 'loaded' && state.groups.length === 0 && (
          <p className="text-sm text-gray-500">
            Ninguna pareja declaró este horario.
          </p>
        )}
        {state.status === 'loaded' &&
          state.groups.map((group) => (
            <section key={group.tournamentId} className="space-y-1">
              <h4 className="text-sm font-semibold text-gray-900">
                {group.name}
                {group.current && ' (actual)'}
                <span className="ml-1 font-normal text-gray-500">
                  ({group.teams.length})
                </span>
              </h4>
              <ul className="space-y-0.5 text-sm text-gray-700">
                {group.teams.map((team) => {
                  const teamGroup = state.teamGroups.get(team.id);
                  return (
                    <li key={team.id}>
                      {teamLabel(team, 'Pareja sin jugadores')}
                      {teamGroup && (
                        <span className="ml-1.5 text-gray-500">
                          · {teamGroup}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
      </DialogContent>
    </Dialog>
  );
}
